import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  SPOT_LAYOUT_PRESET_LIST,
  setSpotLayoutPreset,
} from "./spotLayoutConfig";
import {
  useTradingColors,
  COLOR_PREFERENCES,
  COLOR_PREFERENCE_LIST,
  setTradingColorPreference,
} from "./tradingColorConfig";
import {
  getAvailableExchangeSettings,
  updateUserDefaultSetting,
  LAYOUT_SETTING_MAP,
  COLOR_SETTING_MAP,
  matchColorPreference,
  matchLayoutPreset,
} from "./exchangeSettingsService";
import { showToast } from "../../CoreDataService";

/**
 * Miniature Wireframe Diagram for each preset layout (styled like Binance Order Mode cards)
 */
const LayoutMiniDiagram = ({ presetId, upColor = "#21cb80", downColor = "#f5475d" }) => {
  const bgCard = "#12161c";
  const colTable = "#2b313a";
  const colChart = "#1e2329";
  const colBuy = upColor;
  const colSell = downColor;
  const colSubTable = "#23272e";

  const renderInner = () => {
    switch (presetId) {
      case "DEFAULT":
        return (
          <Box sx={{ display: "flex", gap: "3px", width: "100%", height: "100%" }}>
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colChart, borderRadius: "2px" }} />
              <Box sx={{ height: "10px", display: "flex", gap: "2px" }}>
                <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
                <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
              </Box>
            </Box>
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
          </Box>
        );

      case "BINANCE_PRO":
        return (
          <Box sx={{ display: "flex", gap: "3px", width: "100%", height: "100%" }}>
            <Box sx={{ width: "16px", display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colTable, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colTable, borderRadius: "2px" }} />
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1.3, background: colChart, borderRadius: "2px" }} />
              <Box sx={{ flex: 0.8, background: colSubTable, borderRadius: "2px" }} />
            </Box>
            <Box sx={{ width: "25px", display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
            </Box>
          </Box>
        );

      case "CHART_RIGHT_STACKED_BOTTOM":
        return (
          <Box sx={{ display: "flex", gap: "3px", width: "100%", height: "100%" }}>
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colChart, borderRadius: "2px" }} />
              <Box sx={{ height: "10px", display: "flex", gap: "2px" }}>
                <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
                <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
              </Box>
            </Box>
          </Box>
        );

      case "COINBASE_ADVANCED":
        return (
          <Box sx={{ display: "flex", gap: "3px", width: "100%", height: "100%" }}>
            <Box sx={{ width: "25px", display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
            </Box>
            <Box sx={{ width: "16px", display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colTable, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colTable, borderRadius: "2px" }} />
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1.3, background: colChart, borderRadius: "2px" }} />
              <Box sx={{ flex: 0.8, background: colSubTable, borderRadius: "2px" }} />
            </Box>
          </Box>
        );

      case "FOUR_COLUMN_PANORAMIC":
        return (
          <Box sx={{ display: "flex", gap: "2px", width: "100%", height: "100%" }}>
            <Box sx={{ width: "12px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ width: "12px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ flex: 1, background: colChart, borderRadius: "2px" }} />
            <Box sx={{ width: "24px", display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
            </Box>
          </Box>
        );

      case "CHART_LEFT_INVERTED":
        return (
          <Box sx={{ display: "flex", gap: "3px", width: "100%", height: "100%" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colChart, borderRadius: "2px" }} />
              <Box sx={{ height: "10px", display: "flex", gap: "2px" }}>
                <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
                <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
              </Box>
            </Box>
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
            <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
          </Box>
        );

      case "SWAPPED_BOTTOM_CENTER":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%", height: "100%" }}>
            <Box sx={{ flex: 1, display: "flex", gap: "3px" }}>
              <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
              <Box sx={{ width: "16px", background: colTable, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                <Box sx={{ flex: 1, background: colChart, borderRadius: "2px" }} />
                <Box sx={{ flex: 0.7, background: colSubTable, borderRadius: "2px" }} />
              </Box>
            </Box>
            <Box sx={{ height: "10px", display: "flex", gap: "2px" }}>
              <Box sx={{ flex: 1, background: colBuy, borderRadius: "2px" }} />
              <Box sx={{ flex: 1, background: colSell, borderRadius: "2px" }} />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: 64,
        backgroundColor: bgCard,
        borderRadius: "6px",
        p: "6px",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {renderInner()}
    </Box>
  );
};

const SELECTION_COLOR = "#FFFFFF";

const SpotLayoutDrawer = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState("Layout");
  const [currentPresetId, setCurrentPresetId] = useState(
    () => localStorage.getItem("admin_spot_layout_preset") || "DEFAULT"
  );

  const { preference, upColor, downColor, downBadge, upBadge, label, name: colorName } = useTradingColors();
  const [isColorDropdownOpen, setIsColorDropdownOpen] = useState(false);
  const colorDropdownRef = useRef(null);

  // Dynamic broker-assigned exchange settings
  const [availableLayouts, setAvailableLayouts] = useState(null);
  const [availableColors, setAvailableColors] = useState(null);
  const [defaultLayoutSettingId, setDefaultLayoutSettingId] = useState(null);
  const [defaultColorSettingId, setDefaultColorSettingId] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [actionLoadingSettingId, setActionLoadingSettingId] = useState(null);

  // Fetch dynamic settings from broker admin API
  const loadDynamicSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    try {
      const [layouts, colors] = await Promise.all([
        getAvailableExchangeSettings("Layout"),
        getAvailableExchangeSettings("Color"),
      ]);

      // ── Process Layouts ──
      if (Array.isArray(layouts) && layouts.length > 0) {
        setAvailableLayouts(layouts);
        const defLayout = layouts.find((s) => Number(s.isDefault) === 1);
        if (defLayout) {
          setDefaultLayoutSettingId(defLayout.settingId);
          const targetPreset = matchLayoutPreset(defLayout) || "DEFAULT";
          setCurrentPresetId(targetPreset);
          setSpotLayoutPreset(targetPreset);
        } else {
          setDefaultLayoutSettingId(null);
          // If nothing is set by default in backend: open with Classic layout
          setCurrentPresetId("DEFAULT");
          setSpotLayoutPreset("DEFAULT");
        }
      } else {
        // Fallback: Show all presets if API returns empty
        setAvailableLayouts(null);
      }

      // ── Process Colors ──
      if (Array.isArray(colors) && colors.length > 0) {
        setAvailableColors(colors);
        const defColor = colors.find((s) => Number(s.isDefault) === 1);

        if (defColor) {
          setDefaultColorSettingId(defColor.settingId);
          const targetColor = matchColorPreference(defColor) || "FRESH";
          setTradingColorPreference(targetColor);
        } else {
          setDefaultColorSettingId(null);
          setTradingColorPreference("FRESH");
        }
      } else {
        setAvailableColors(null);
      }
    } catch (err) {
      console.error("[SpotLayoutDrawer] Failed to load dynamic settings:", err);
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  // Fetch whenever drawer opens
  useEffect(() => {
    if (open) {
      loadDynamicSettings();
    }
  }, [open, loadDynamicSettings]);

  // Close color dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        colorDropdownRef.current &&
        !colorDropdownRef.current.contains(e.target)
      ) {
        setIsColorDropdownOpen(false);
      }
    };
    if (isColorDropdownOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isColorDropdownOpen]);

  // Keep preset ID in sync with external layout changes
  useEffect(() => {
    const handleLayoutUpdate = () => {
      const activeId = localStorage.getItem("admin_spot_layout_preset") || "DEFAULT";
      setCurrentPresetId(activeId);
    };

    window.addEventListener("spot_layout_change", handleLayoutUpdate);
    return () => {
      window.removeEventListener("spot_layout_change", handleLayoutUpdate);
    };
  }, []);

  // User selects layout preset
  const handleSelectPreset = async (preset) => {
    setCurrentPresetId(preset.id);
    setSpotLayoutPreset(preset.id);

    // Save as default in backend
    if (preset.settingId) {
      setActionLoadingSettingId(preset.settingId);
      try {
        const res = await updateUserDefaultSetting(preset.settingId);
        if (res?.errorCode === 0 || !res?.error) {
          setDefaultLayoutSettingId(preset.settingId);
          showToast("success", `${preset.name} set as default layout`);
        }
      } catch (err) {
        console.error("Failed to update user default layout:", err);
      } finally {
        setActionLoadingSettingId(null);
      }
    }
  };

  // User selects color preference
  const handleColorSelect = async (item) => {
    setTradingColorPreference(item.id);
    setIsColorDropdownOpen(false);

    // Save as default in backend
    if (item.settingId) {
      setActionLoadingSettingId(item.settingId);
      try {
        const res = await updateUserDefaultSetting(item.settingId);
        if (res?.errorCode === 0 || !res?.error) {
          setDefaultColorSettingId(item.settingId);
          showToast("success", `${item.displayName || item.name || item.label} set as default color`);
        }
      } catch (err) {
        console.error("Failed to update user default color:", err);
      } finally {
        setActionLoadingSettingId(null);
      }
    }
  };

  // ── Compute visible presets & colors based on broker-activated list ──────
  const visiblePresets = availableLayouts
    ? availableLayouts
        .map((s) => {
          const presetKey = matchLayoutPreset(s);
          const basePreset = SPOT_LAYOUT_PRESET_LIST.find((p) => p.id === presetKey);
          if (!basePreset) return null;
          return {
            ...basePreset,
            settingId: s.settingId,
            apiName: s.name,
          };
        })
        .filter(Boolean)
    : SPOT_LAYOUT_PRESET_LIST;

  const visibleColors = availableColors
    ? availableColors
        .map((s) => {
          const prefKey = matchColorPreference(s);
          if (!prefKey || !COLOR_PREFERENCES[prefKey]) return null;
          return {
            ...COLOR_PREFERENCES[prefKey],
            settingId: s.settingId,
            apiName: s.name,
            displayName: s.name || COLOR_PREFERENCES[prefKey].name,
          };
        })
        .filter(Boolean)
    : COLOR_PREFERENCE_LIST.map((c) => ({
        ...c,
        displayName: c.name || c.label,
      }));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 380 },
          backgroundColor: "#181a20",
          color: "#eaecef",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.65)",
          borderLeft: "1px solid rgba(255,255,255,0.08)",
          p: 0,
        },
      }}
    >
      {/* ── Top Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <Box
            onClick={() => setActiveTab("Layout")}
            sx={{
              position: "relative",
              pb: 0.5,
              cursor: "pointer",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: activeTab === "Layout" ? 700 : 500,
                color: activeTab === "Layout" ? "#eaecef" : "#848e9c",
                transition: "color 0.15s ease",
                "&:hover": { color: "#eaecef" },
              }}
            >
              Layout & Colors
            </Typography>
            {activeTab === "Layout" && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: -13,
                  left: 0,
                  right: 0,
                  height: "2.5px",
                  backgroundColor: "#F0B90B",
                  borderRadius: "2px",
                }}
              />
            )}
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#848e9c",
            "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.06)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* ── Drawer Body ── */}
      <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
        {/* Style Subheading */}
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: "#848e9c",
            mb: 1.5,
            fontWeight: 500,
          }}
        >
          Style
        </Typography>

        {/* ── Color Preference Row (Binance Style) ── */}
        <Box
          ref={colorDropdownRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0,
            position: "relative",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#eaecef",
            }}
          >
            Color Preference
          </Typography>

          <Box
            onClick={() => setIsColorDropdownOpen(!isColorDropdownOpen)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 1.2,
              py: 0.6,
              borderRadius: "6px",
              backgroundColor: isColorDropdownOpen
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.12)",
              cursor: "pointer",
              userSelect: "none",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "2.5px" }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: downBadge,
                }}
              />
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  backgroundColor: upBadge,
                }}
              />
            </Box>

            <Typography sx={{ fontSize: "0.78rem", color: "#eaecef", fontWeight: 500 }}>
              {visibleColors.find((c) => c.id === preference)?.displayName || colorName || label}
            </Typography>

            <KeyboardArrowDownIcon
              sx={{
                fontSize: 16,
                color: "#848e9c",
                transform: isColorDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s ease",
              }}
            />
          </Box>

          {/* ── Popover Dropdown ── */}
          {isColorDropdownOpen && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                zIndex: 1400,
                minWidth: 230,
                backgroundColor: "#1e2329",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: "8px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.6)",
                p: 0.8,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {visibleColors.map((item) => {
                const isSelected = item.id === preference;
                const isDefault = defaultColorSettingId === item.settingId;
                const isLoading = actionLoadingSettingId === item.settingId;

                return (
                  <Box
                    key={item.id}
                    onClick={() => !isLoading && handleColorSelect(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1,
                      borderRadius: "6px",
                      cursor: isLoading ? "wait" : "pointer",
                      backgroundColor: isSelected
                        ? "rgba(255, 255, 255, 0.05)"
                        : "transparent",
                      transition: "background-color 0.12s ease",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                        <Box
                          sx={{
                            width: 11,
                            height: 11,
                            borderRadius: "2px",
                            backgroundColor: item.downBadge,
                          }}
                        />
                        <Box
                          sx={{
                            width: 11,
                            height: 11,
                            borderRadius: "2px",
                            backgroundColor: item.upBadge,
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? "#eaecef" : "#b7bdc6",
                          }}
                        >
                          {item.displayName || item.name || item.label}
                        </Typography>
                        {isDefault && (
                          <Typography
                            sx={{
                              fontSize: "0.62rem",
                              color: "#F0B90B",
                              fontWeight: 600,
                              textTransform: "uppercase",
                              lineHeight: 1,
                              mt: 0.2,
                            }}
                          >
                            Default
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {isLoading ? (
                      <CircularProgress size={14} sx={{ color: "#F0B90B" }} />
                    ) : isSelected ? (
                      <CheckIcon sx={{ fontSize: 16, color: "#F0B90B" }} />
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Style Settings Section Heading */}
        <Typography
          sx={{
            fontSize: "0.8rem",
            color: "#848e9c",
            mt: 3,
            mb: 1.5,
            fontWeight: 500,
          }}
        >
          Style Settings
        </Typography>

        {/* Section Heading: Order Mode */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: "0.88rem",
              fontWeight: 700,
              color: "#eaecef",
              letterSpacing: "0.01em",
            }}
          >
            Order Mode
          </Typography>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#848e9c",
              mt: 0.4,
            }}
          >
            Select your preferred trading dashboard workspace arrangement
          </Typography>
        </Box>

        {/* ── Loading Spinner or Preset Cards Grid ── */}
        {isLoadingSettings ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={26} sx={{ color: "#F0B90B" }} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1.5,
              mb: 3.5,
            }}
          >
            {visiblePresets.map((preset) => {
              const isSelected = preset.id === currentPresetId;
              const isDefault = defaultLayoutSettingId === preset.settingId;
              const isLoading = actionLoadingSettingId === preset.settingId;

              return (
                <Box
                  key={preset.id}
                  onClick={() => !isLoading && handleSelectPreset(preset)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: isLoading ? "wait" : "pointer",
                    userSelect: "none",
                  }}
                >
                  {/* Visual Card Container */}
                  <Box
                    sx={{
                      width: "100%",
                      p: "4px",
                      borderRadius: "8px",
                      border: isSelected
                        ? `2px solid ${SELECTION_COLOR}`
                        : "1.5px solid rgba(255,255,255,0.12)",
                      backgroundColor: isSelected
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(255,255,255,0.02)",
                      boxShadow: isSelected
                        ? "0 0 16px rgba(255, 255, 255, 0.25)"
                        : "none",
                      transition: "all 0.2s ease",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        borderColor: isSelected
                          ? SELECTION_COLOR
                          : "rgba(255,255,255,0.35)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <LayoutMiniDiagram
                      presetId={preset.id}
                      upColor={upColor}
                      downColor={downColor}
                    />

                    {/* Active Checkmark Pill in corner */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 3,
                          right: 3,
                          backgroundColor: "#181a20",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 2,
                        }}
                      >
                        <CheckCircleIcon sx={{ fontSize: 14, color: SELECTION_COLOR }} />
                      </Box>
                    )}

                    {/* Default Indicator Pill in bottom corner */}
                    {isDefault && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 3,
                          left: 3,
                          backgroundColor: "rgba(240, 185, 11, 0.9)",
                          color: "#000",
                          borderRadius: "3px",
                          px: "4px",
                          py: "1px",
                          fontSize: "0.55rem",
                          fontWeight: 800,
                          letterSpacing: "0.03em",
                          textTransform: "uppercase",
                          zIndex: 2,
                        }}
                      >
                        Default
                      </Box>
                    )}

                    {/* Loading overlay when updating default */}
                    {isLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          backgroundColor: "rgba(0,0,0,0.6)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 5,
                        }}
                      >
                        <CircularProgress size={16} sx={{ color: "#F0B90B" }} />
                      </Box>
                    )}
                  </Box>

                  {/* Card Label */}
                  <Typography
                    sx={{
                      mt: 0.8,
                      fontSize: "0.75rem",
                      fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? SELECTION_COLOR : "#b7bdc6",
                      textAlign: "center",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                    }}
                  >
                    {preset.apiName || preset.name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default SpotLayoutDrawer;
